import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import apiService from '../services/apiService';

export type ThemePreset = 'mediterranean' | 'slate-pro' | 'forest' | 'plum';
export type ThemeMode = 'light' | 'dark' | 'auto';

export interface BrandingSettings {
  practice_name: string;
  header_tagline: string;
  logo_url: string | null;
  theme_preset: ThemePreset;
  theme_mode: ThemeMode;
}

interface BrandingContextValue extends BrandingSettings {
  loading: boolean;
  isDark: boolean;
  refresh: () => Promise<void>;
  update: (patch: Partial<Pick<BrandingSettings, 'practice_name' | 'header_tagline' | 'theme_preset' | 'theme_mode'>>) => Promise<void>;
  uploadLogo: (file: File) => Promise<void>;
  removeLogo: () => Promise<void>;
}

const DEFAULT: BrandingSettings = {
  practice_name: 'SeaDent',
  header_tagline: 'Appointment Manager',
  logo_url: null,
  theme_preset: 'mediterranean',
  theme_mode: 'light',
};

const BrandingContext = createContext<BrandingContextValue | null>(null);

const isMode = (v: unknown): v is ThemeMode => v === 'light' || v === 'dark' || v === 'auto';
const isPreset = (v: unknown): v is ThemePreset =>
  v === 'mediterranean' || v === 'slate-pro' || v === 'forest' || v === 'plum';

const resolveDark = (mode: ThemeMode): boolean => {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const applyToDom = (settings: BrandingSettings, isDark: boolean) => {
  const shells = document.querySelectorAll<HTMLElement>('[data-admin-shell]');
  shells.forEach((el) => {
    el.dataset.adminTheme = settings.theme_preset;
    el.classList.toggle('dark', isDark);
  });
};

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<BrandingSettings>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(false);
  const lastModeRef = useRef<ThemeMode>(DEFAULT.theme_mode);

  const apply = useCallback((next: BrandingSettings) => {
    const dark = resolveDark(next.theme_mode);
    setState(next);
    setIsDark(dark);
    lastModeRef.current = next.theme_mode;
    applyToDom(next, dark);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const resp = await apiService.get<BrandingSettings>('/settings/branding');
      const data = resp.data?.data;
      if (data) {
        apply({
          practice_name: data.practice_name || DEFAULT.practice_name,
          header_tagline: typeof data.header_tagline === 'string' ? data.header_tagline : DEFAULT.header_tagline,
          logo_url: data.logo_url ?? null,
          theme_preset: isPreset(data.theme_preset) ? data.theme_preset : DEFAULT.theme_preset,
          theme_mode: isMode(data.theme_mode) ? data.theme_mode : DEFAULT.theme_mode,
        });
      }
    } catch (e) {
      // Likely unauthenticated or first run — keep defaults silently.
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Track OS theme changes only while in "auto" mode.
  useEffect(() => {
    if (lastModeRef.current !== 'auto') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (lastModeRef.current === 'auto') {
        const dark = mq.matches;
        setIsDark(dark);
        applyToDom(state, dark);
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [state]);

  const update = useCallback(
    async (patch: Partial<Pick<BrandingSettings, 'practice_name' | 'theme_preset' | 'theme_mode'>>) => {
      const resp = await apiService.put<BrandingSettings>('/settings/branding', patch);
      const data = resp.data?.data;
      if (data) {
        apply({
          ...state,
          ...data,
        });
      }
    },
    [apply, state]
  );

  const uploadLogo = useCallback(
    async (file: File) => {
      const form = new FormData();
      form.append('logo', file);
      const token = localStorage.getItem('admin_token');
      const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';
      const resp = await axios.post<{ success: boolean; data: BrandingSettings; message?: string }>(
        `${base}/settings/branding/logo`,
        form,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = resp.data?.data;
      if (data) {
        // Bust cache by appending a fresh query param to the URL we just got back.
        apply({ ...state, ...data, logo_url: data.logo_url ? `${data.logo_url}&t=${Date.now()}` : null });
      }
    },
    [apply, state]
  );

  const removeLogo = useCallback(async () => {
    const resp = await apiService.delete<BrandingSettings>('/settings/branding/logo');
    const data = resp.data?.data;
    if (data) apply({ ...state, ...data });
  }, [apply, state]);

  const value = useMemo<BrandingContextValue>(
    () => ({
      ...state,
      loading,
      isDark,
      refresh,
      update,
      uploadLogo,
      removeLogo,
    }),
    [state, loading, isDark, refresh, update, uploadLogo, removeLogo]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = (): BrandingContextValue => {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used inside BrandingProvider');
  return ctx;
};
